import Phaser from 'phaser';

/** Shared pastel palette + typography so every screen reads as one consistent,
 * cheerful visual system instead of ad-hoc colors per scene. */
export const PALETTE = {
  bgTop: 0x2e2050,
  bgBottom: 0x150e26,
  panel: 0x3a2a5e,
  panelLight: 0x4d3878,
  primary: 0xff8fb1,
  primaryDark: 0xe86f95,
  secondary: 0x8ee3ff,
  accent: 0xffe066,
  success: 0x7ee787,
  textLight: 0xfff6fb,
  textMuted: 0xcbb9e8,
  danger: 0xff6b6b,
} as const;

export const FONT_FAMILY = "'Baloo 2', 'Comic Sans MS', system-ui, sans-serif";

export function titleTextStyle(size = 56): Phaser.Types.GameObjects.Text.TextStyle {
  return {
    fontFamily: FONT_FAMILY,
    fontSize: `${size}px`,
    color: '#fff6fb',
    fontStyle: '700',
    stroke: '#5a3b82',
    strokeThickness: 8,
  };
}

export function bodyTextStyle(size = 28, color = '#fff6fb'): Phaser.Types.GameObjects.Text.TextStyle {
  return {
    fontFamily: FONT_FAMILY,
    fontSize: `${size}px`,
    color,
  };
}

/** Draws a rounded, drop-shadowed pastel button and returns an interactive container.
 * Reused across every menu screen so buttons feel/behave identically everywhere. */
export function createButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  label: string,
  color: number = PALETTE.primary,
  fontSize = 32
): Phaser.GameObjects.Container {
  const container = scene.add.container(x, y);
  const radius = Math.min(24, height / 2);

  const shadow = scene.add.graphics();
  shadow.fillStyle(0x000000, 0.25);
  shadow.fillRoundedRect(-width / 2, -height / 2 + 6, width, height, radius);

  const bg = scene.add.graphics();
  bg.fillStyle(color, 1);
  bg.fillRoundedRect(-width / 2, -height / 2, width, height, radius);
  bg.lineStyle(3, 0xffffff, 0.55);
  bg.strokeRoundedRect(-width / 2, -height / 2, width, height, radius);

  const text = scene.add.text(0, 0, label, bodyTextStyle(fontSize)).setOrigin(0.5);
  text.setFontStyle('700');

  container.add([shadow, bg, text]);
  container.setSize(width, height);
  container.setInteractive({ useHandCursor: true });

  container.on('pointerover', () => container.setScale(1.03));
  container.on('pointerout', () => container.setScale(1));
  container.on('pointerdown', () => {
    scene.tweens.add({ targets: container, scale: 0.94, duration: 70, yoyo: true });
  });

  return container;
}

/** Draws the small pill-shaped coin counter reused on Home and both shop screens, so
 * a purchase's coin-draining animation (see animateCoinSpend) reads consistently
 * everywhere it can happen. Returns the Text so callers can tween its value. */
export function createCoinPanel(scene: Phaser.Scene, x: number, y: number, coins: number): Phaser.GameObjects.Text {
  const panel = scene.add.container(x, y);
  const bg = scene.add.graphics();
  bg.fillStyle(0x000000, 0.35);
  bg.fillRoundedRect(-90, -26, 180, 52, 26);
  const icon = scene.add.circle(-58, 0, 16, 0xffe066).setStrokeStyle(2, 0xffffff);
  const text = scene.add.text(-30, 0, `${coins}`, bodyTextStyle(24)).setOrigin(0, 0.5);
  panel.add([bg, icon, text]);
  return text;
}

/** Animates a coin counter draining from its current value down to `newTotal`,
 * playing an unlock chime alongside it, so spending coins on a cat/background feels
 * tactile instead of the total just silently jumping on scene restart. */
export function animateCoinSpend(
  scene: Phaser.Scene,
  coinText: Phaser.GameObjects.Text,
  fromTotal: number,
  toTotal: number,
  onComplete: () => void
): void {
  const counter = { value: fromTotal };
  scene.tweens.add({
    targets: counter,
    value: toTotal,
    duration: 600,
    ease: 'Cubic.easeOut',
    onUpdate: () => coinText.setText(`${Math.round(counter.value)}`),
    onComplete,
  });
}
